import {
  useCallback,
  useEffect,
  useState
} from 'react';
import {
  connectAgentWebRtcClient,
  disconnectAgentWebRtcClient,
  muteCurrentAgentCall,
  unmuteCurrentAgentCall,
  isCurrentAgentCallMuted,
  isAgentClientConnected,
  getCurrentAgentCall,
  subscribeAgentSdkEvents
} from '../infrastructure/webrtc/voiceAgentWebRtcAdapter';

/**
 * FreeSWITCH / JsSIP agent media hook (one SIP registration per browser tab).
 *
 * @returns {{
 *   isConnected: boolean,
 *   isMuted: boolean,
 *   connect: () => Promise<void>,
 *   disconnect: () => Promise<void>,
 *   mute: () => boolean,
 *   unmute: () => boolean,
 *   currentCall: import('jssip').RTCSession|null
 * }}
 */
export default function useVoiceWebRTC() {
  const [isConnected, setIsConnected] = useState(isAgentClientConnected());
  const [isMuted, setIsMuted] = useState(isCurrentAgentCallMuted());
  const [currentCall, setCurrentCall] = useState(getCurrentAgentCall());

  useEffect(() => {
    const unsubscribe = subscribeAgentSdkEvents((event) => {
      if (event.type === 'ready' || event.type === 'disconnected') {
        setIsConnected(isAgentClientConnected());
      }
      if (event.type === 'incoming' || event.type === 'outgoing' || event.type === 'ended') {
        setCurrentCall(getCurrentAgentCall());
        setIsMuted(isCurrentAgentCallMuted());
      }
    });
    return unsubscribe;
  }, []);

  const connect = useCallback(async () => {
    await connectAgentWebRtcClient();
    setIsConnected(isAgentClientConnected());
    setCurrentCall(getCurrentAgentCall());
    setIsMuted(isCurrentAgentCallMuted());
  }, []);

  const disconnect = useCallback(async () => {
    await disconnectAgentWebRtcClient();
    setIsConnected(false);
    setCurrentCall(null);
    setIsMuted(false);
  }, []);

  const mute = useCallback(() => {
    const didMute = muteCurrentAgentCall();
    if (didMute) setIsMuted(true);
    return didMute;
  }, []);

  const unmute = useCallback(() => {
    const didUnmute = unmuteCurrentAgentCall();
    if (didUnmute) setIsMuted(false);
    return didUnmute;
  }, []);

  return {
    isConnected,
    isMuted,
    connect,
    disconnect,
    mute,
    unmute,
    currentCall
  };
}
